import * as React from 'react';
import { withTheme } from 'styled-components';
import { ContainerGutter, styled, ThemeDefinition, GalleryContext, Container } from '@volkswagen-onehub/components-core';

const StyledImages = styled.div`
	display: flex;
	justify-content: center;
`;

const StyledImage = styled.button<{
	readonly isActive: boolean;
}>`
	position: relative;
	border: 0; // reset browser styles
	padding: 0; // reset browser styles
	background: ${({ theme }: { theme: any }) => theme.colors.background.primary};
	cursor: ${({ isActive }: { isActive: any }) => (isActive ? 'unset' : 'pointer')};
	z-index: ${({ isActive }: { isActive: any }) => (isActive ? '1' : 'unset')};
	outline: none; // reset browser styles
	transform: scale(${({ isActive }: { isActive: any }) => (isActive ? 1.2 : 1)});
	transition: ${({ theme }: { theme: any }) => {
		const duration = theme.animation.duration.layer;
		const timingFunction = theme.animation.timingFunction.in;

		return `all ${duration} ${timingFunction}`;
	}};

	:focus {
		outline: 1px solid ${({ theme }: { theme: any }) => theme.colors.border.hover};
		outline-offset: 2px;
	}

	:focus,
	:hover {
		::before {
			background: ${({ theme }: { theme: any }) => theme.colors.button.primary.active};
		}
	}
`;

export type NavigationImagesProps = {
	readonly theme: ThemeDefinition;
};

class InternalNavigationImages extends React.PureComponent<React.PropsWithChildren<NavigationImagesProps>> {
	private readonly carouselRefs: {
		[id: string]: React.RefObject<HTMLButtonElement>;
	} = {};

	private createCarouselRef(elementId: string): React.RefObject<HTMLElement> | void {
		// Do not recreate the ref if it already exists
		if (Object.hasOwn(this.carouselRefs, elementId)) {
			return;
		}

		this.carouselRefs[elementId] = React.createRef();

		// eslint-disable-next-line consistent-return
		return this.carouselRefs[elementId];
	}

	private readonly focusElement = (el: string) => {
		const element = this.carouselRefs[el].current;
		if (element) {
			element.focus();
		}
	};

	private readonly createKeyHandler = (triggerChange: (newIndex: number) => void, index: number) => (event: React.KeyboardEvent) => {
		const { children, theme } = this.props;
		const length = React.Children.count(children);

		let newIndex: number | null;
		switch (event.key) {
			case 'ArrowLeft':
				newIndex = (index + (theme.direction === 'rtl' ? 1 : length - 1)) % length;
				break;
			case 'ArrowRight':
				newIndex = (index + (theme.direction === 'rtl' ? length - 1 : 1)) % length;
				break;
			case 'Home':
				newIndex = 0;
				break;
			case 'End':
				newIndex = length - 1;
				break;
			default:
				newIndex = null;
		}

		if (newIndex !== null) {
			event.stopPropagation();
			event.preventDefault();
			this.focusElement(`bullet-${newIndex}`);
			triggerChange(newIndex);
		}
	};

	// eslint-disable-next-line class-methods-use-this
	private readonly createClickHandler = (triggerChange: (newIndex: number) => void, index: number) => (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		triggerChange(index);
	};

	public render(): React.ReactElement {
		const { children } = this.props;

		return (
			<GalleryContext.Consumer>
				{({ triggerChange, index }) => (
					<StyledImages>
						<Container wrap="never" gutter={ContainerGutter.static150}>
							{React.Children.map(children, (child, bulletIndex) => {
								this.createCarouselRef(`bullet-${bulletIndex}`);
								const { key } = child as { key: string };
								return (
									<StyledImage
										isActive={index === bulletIndex}
										onClick={this.createClickHandler(triggerChange, bulletIndex)}
										onKeyDown={this.createKeyHandler(triggerChange, bulletIndex)}
										key={key}
										ref={this.carouselRefs[`bullet-${bulletIndex}`]}
										tabIndex={index === bulletIndex ? 0 : -1}
									>
										{child}
									</StyledImage>
								);
							})}
						</Container>
					</StyledImages>
				)}
			</GalleryContext.Consumer>
		);
	}
}

export const NavigationImages: React.FC<NavigationImagesProps> = withTheme(InternalNavigationImages);
